import { ITelemetry, QosResultData } from '../..';
import { getErrorJsonData } from '../../sinks/utils/getErrorJsonData';
import { QosState } from './types';

export interface ITelemetryQos {
  /**
   * The current QoS state.
   */
  readonly state: QosState;
  /**
   * A feature has successfully loaded. This is a no-op if called more than
   * once, or after any other method.
   */
  complete(data?: QosResultData): void;
  /**
   * A feature has encountered an error. This is a no-op if called more than
   * once, or after `timeout`. It may be called after
   * {@link complete} to report an _update_ failure.
   */
  failure(error: unknown, failedComponent?: string): void;
  /**
   * Feature did not load within the expected time limit. This is a no-op if
   * called more than once, or after any other method.
   */
  timeout(data?: QosResultData): void;
}

/**
 * __For internal use only!__
 *
 * Create {@link ITelemetryQos} instances.
 *
 * Creates a stateful QoS telemetry reporter instance which enforces the correct QoS
 * telemetry event pattern for loading a single instance of a single feature.
 */
export function createQosTelemetryReporter(telemetry: ITelemetry, target: string): ITelemetryQos {
  const startTime = Date.now();

  let state = QosState.IDLE;

  return {
    get state() {
      return state;
    },
    complete: () => {
      if (state === QosState.IDLE) {
        state = QosState.LOAD_COMPLETE;
        telemetry.reportQos(target, {
          action: state,
          loadTime: Math.max(0, Date.now() - startTime)
        });
      }
    },
    failure: (error, failedComponent?: string) => {
      // Always log the actual error so we won't lose error logs
      telemetry.error(`ComponentQoS/${failedComponent || 'thrown-error'}`, error);

      if (state === QosState.IDLE) {
        state = QosState.LOAD_FAILURE;
      } else if (state === QosState.LOAD_COMPLETE) {
        state = QosState.UPDATE_FAILURE;
      } else {
        return;
      }

      telemetry.reportQos(target, {
        action: state,
        error: getErrorJsonData(error)
      });
    },
    timeout: () => {
      if (state === QosState.IDLE) {
        state = QosState.TIMEOUT;
        telemetry.reportQos(target, {
          action: QosState.TIMEOUT
        });
      }
    }
  };
}
